import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import './App.css';
import Tournaments from './pages/Tournaments';
import TournamentDetails from './pages/TournamentDetails';
import Dashboard from './pages/Dashboard';
import Profile from './pages/Profile';
import LiveMatches from './components/LiveMatches';
import PickemPage from './pages/PickemPage';
import BudgetLeaguePage from './pages/BudgetLeaguePage';
import BudgetLeagueOverview from './pages/BudgetLeagueOverview';
import DraftLeaguePage from './pages/DraftLeaguePage';
import H2HPage from './pages/H2HPage';
import H2HLeaguePage from './pages/H2HLeaguePage';
import EliminatorPage from './pages/EliminatorPage';
import CreateLeaguePage from './pages/CreateLeaguePage';
import PickemLeaguePage from './pages/PickemLeaguePage';
import EliminatorLeaguePage from './pages/EliminatorLeaguePage';
import JoinLeaguePage from './pages/JoinLeaguePage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import MyLeagues from './components/MyLeagues';
import { apiService } from './services/api';
import Header from './components/Header';
import AuthModals from './components/AuthModals';
import { AuthProvider, useAuth } from './hooks/AuthContext';
import AdminTournamentDetails from './pages/AdminTournamentDetails';
import CreateTournamentModal from './components/CreateTournamentModal';

// Enhanced AdminPanel component with sidebar navigation
const AdminPanel = () => {
  const [activePage, setActivePage] = useState<'tournaments' | 'fetch-api' | 'leagues'>('tournaments');
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7]">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 bg-white/10 backdrop-blur-md border-r border-white/20 min-h-screen">
          <div className="p-6">
            <h2 className="text-2xl font-bold text-white mb-8">Admin Panel</h2>
            <nav className="space-y-2">
              <button
                onClick={() => setActivePage('tournaments')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  activePage === 'tournaments'
                    ? 'bg-accent text-primary font-semibold'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                🏆 Tournaments
              </button>
              <button
                onClick={() => setActivePage('fetch-api')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  activePage === 'fetch-api'
                    ? 'bg-accent text-primary font-semibold'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                🔌 Fetch API
              </button>
              <button
                onClick={() => setActivePage('leagues')}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors ${
                  activePage === 'leagues'
                    ? 'bg-accent text-primary font-semibold'
                    : 'text-white hover:bg-white/10'
                }`}
              >
                🏆 League Management
              </button>
            </nav>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 p-8">
          {activePage === 'tournaments' && <TournamentsPage />}
          {activePage === 'fetch-api' && <FetchAPIPage />}
          {activePage === 'leagues' && <LeagueManagementPage />}
        </div>
      </div>
    </div>
  );
};

// LeagueManagementPage component
const LeagueManagementPage = () => {
  const [activeTab, setActiveTab] = useState<'pickem' | 'eliminator' | 'h2h' | 'budget' | 'draft'>('pickem');
  const [leagues, setLeagues] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedLeague, setSelectedLeague] = useState<any>(null);
  const [showLeagueDetails, setShowLeagueDetails] = useState(false);

  const fetchLeagues = async (leagueType: string) => {
    try {
      setLoading(true);
      const data = await apiService.getAllLeaguesAdmin(leagueType);
      setLeagues(data.leagues || []);
    } catch (err: any) {
      setError(err.message || `Failed to load ${leagueType} leagues`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeagues(activeTab);
  }, [activeTab]);

  const handleDeleteLeague = async (leagueId: number) => {
    if (!window.confirm('Are you sure you want to delete this league? This action cannot be undone.')) {
      return;
    }

    setUpdatingId(leagueId);
    try {
      await apiService.deleteLeagueAdmin(leagueId);
      await fetchLeagues(activeTab);
    } catch (err: any) {
      alert('Failed to delete league: ' + (err.message || 'Unknown error'));
    }
    setUpdatingId(null);
  };

  const handlePromoteManager = async (leagueId: number, userId: number) => {
    try {
      await apiService.promoteLeagueManager(leagueId, userId);
      await fetchLeagues(activeTab);
    } catch (err: any) {
      alert('Failed to promote manager: ' + (err.message || 'Unknown error'));
    }
  };

  const handleViewLeagueDetails = (league: any) => {
    setSelectedLeague(league);
    setShowLeagueDetails(true);
  };

  const renderLeagueTable = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left py-4 px-6 text-primary font-bold">League Name</th>
              <th className="text-left py-4 px-6 text-primary font-bold">Tournament</th>
              <th className="text-left py-4 px-6 text-primary font-bold">Manager</th>
              <th className="text-center py-4 px-6 text-primary font-bold">Participants</th>
              <th className="text-center py-4 px-6 text-primary font-bold">Status</th>
              <th className="text-center py-4 px-6 text-primary font-bold">Created</th>
              <th className="text-center py-4 px-6 text-primary font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {leagues.map((league) => (
              <tr key={league.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                <td className="py-4 px-6">
                  <div>
                    <p className="font-medium text-primary">{league.name}</p>
                    <p className="text-sm text-gray-500">
                      {league.is_public ? 'Public' : 'Private'}
                      {league.join_code && ` • Code: ${league.join_code}`}
                    </p>
                  </div>
                </td>
                <td className="py-4 px-6">
                  <p className="text-primary">{league.tournament_name}</p>
                  <p className="text-sm text-gray-500">{league.format} • {league.year}</p>
                </td>
                <td className="py-4 px-6">
                  <p className="text-primary">{league.manager_name}</p>
                  <p className="text-sm text-gray-500">{league.manager_email}</p>
                </td>
                <td className="text-center py-4 px-6">
                  <span className="font-semibold text-primary">
                    {league.current_participants}/{league.max_participants}
                  </span>
                </td>
                <td className="text-center py-4 px-6">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                    league.status === 'active' 
                      ? 'bg-green-100 text-green-800'
                      : 'bg-yellow-100 text-yellow-800'
                  }`}>
                    {league.status}
                  </span>
                </td>
                <td className="text-center py-4 px-6 text-gray-600">
                  {new Date(league.created_at).toLocaleDateString()}
                </td>
                <td className="text-center py-4 px-6">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      onClick={() => handleDeleteLeague(league.id)}
                      disabled={updatingId === league.id}
                      className="text-red-600 hover:text-red-800 transition-colors duration-200"
                      title="Delete League"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleViewLeagueDetails(league)}
                      className="text-blue-600 hover:text-blue-800 transition-colors duration-200"
                      title="View League Details"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-white">League Management</h1>
        <div className="text-white">
          Total Leagues: <span className="font-bold">{leagues.length}</span>
        </div>
      </div>

      {/* League Type Tabs */}
      <div className="flex space-x-1 bg-white/10 backdrop-blur-sm rounded-lg p-1 border border-white/20">
        {[
          { key: 'pickem', label: 'Pick\'em', icon: '🎯' },
          { key: 'eliminator', label: 'Eliminator', icon: '⚡' },
          { key: 'h2h', label: 'H2H', icon: '🤝' },
          { key: 'budget', label: 'Budget', icon: '💰' },
          { key: 'draft', label: 'Draft', icon: '📋' }
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key as any)}
            className={`flex-1 py-3 px-4 rounded-md font-medium transition-all duration-200 ${
              activeTab === tab.key
                ? 'bg-accent text-primary shadow-lg transform scale-105'
                : 'text-white hover:text-accent hover:bg-white/10'
            }`}
          >
            <span className="mr-2">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="space-y-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
            <p className="text-white mt-4">Loading {activeTab} leagues...</p>
          </div>
        ) : error ? (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-6">
            <h3 className="text-red-400 font-semibold mb-2">Error</h3>
            <p className="text-red-300">{error}</p>
          </div>
        ) : leagues.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-white text-lg">No {activeTab} leagues found.</p>
            <p className="text-gray-300 mt-2">Leagues will appear here once users create them.</p>
          </div>
        ) : (
          renderLeagueTable()
        )}
      </div>

      {/* League Details Modal */}
      {showLeagueDetails && selectedLeague && (
        <LeagueDetailsModal
          league={selectedLeague}
          onClose={() => {
            setShowLeagueDetails(false);
            setSelectedLeague(null);
          }}
          onRefresh={() => fetchLeagues(activeTab)}
        />
      )}
    </div>
  );
};

// LeagueDetailsModal component
const LeagueDetailsModal = ({ league, onClose, onRefresh }: { league: any; onClose: () => void; onRefresh: () => void }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'participants' | 'settings'>('overview');
  const [participants, setParticipants] = useState<any[]>([]);
  const [settings, setSettings] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [updatingParticipant, setUpdatingParticipant] = useState<number | null>(null);

  useEffect(() => {
    if (league) {
      loadLeagueData();
    }
  }, [league]);

  const loadLeagueData = async () => {
    setLoading(true);
    try {
      // Load participants
      const participantsData = await apiService.getLeagueParticipantsAdmin(league.id);
      setParticipants(participantsData.participants || []);

      // Load settings
      const settingsData = await apiService.getLeagueSettingsAdmin(league.id);
      console.log('Settings data received:', settingsData);
      setSettings(settingsData.settings || {});
    } catch (err: any) {
      console.error('Failed to load league data:', err);
      setSettings(null); // Reset settings on error
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveParticipant = async (participantId: number) => {
    if (!window.confirm('Are you sure you want to remove this participant from the league?')) {
      return;
    }

    setUpdatingParticipant(participantId);
    try {
      await apiService.removeLeagueParticipantAdmin(league.id, participantId);
      await loadLeagueData();
      onRefresh();
    } catch (err: any) {
      alert('Failed to remove participant: ' + (err.message || 'Unknown error'));
    }
    setUpdatingParticipant(null);
  };

  const handlePromoteToAdmin = async (participantId: number) => {
    if (!window.confirm('Are you sure you want to promote this user to admin?')) {
      return;
    }

    setUpdatingParticipant(participantId);
    try {
      await apiService.promoteLeagueManager(league.id, participantId);
      await loadLeagueData();
      onRefresh();
    } catch (err: any) {
      alert('Failed to promote user: ' + (err.message || 'Unknown error'));
    }
    setUpdatingParticipant(null);
  };

  const handleRemoveAdmin = async (participantId: number) => {
    // Check if this is the only admin
    const admins = participants.filter(p => p.role === 'manager');
    if (admins.length === 1) {
      alert('Cannot remove the only admin. Please promote another user to admin first.');
      return;
    }

    if (!window.confirm('Are you sure you want to remove this user from admin role?')) {
      return;
    }

    setUpdatingParticipant(participantId);
    try {
      await apiService.removeLeagueAdmin(league.id, participantId);
      await loadLeagueData();
      onRefresh();
    } catch (err: any) {
      alert('Failed to remove admin: ' + (err.message || 'Unknown error'));
    }
    setUpdatingParticipant(null);
  };

  const handleUpdateSettings = async (newSettings: any) => {
    try {
      await apiService.updateLeagueSettingsAdmin(league.id, newSettings);
      await loadLeagueData();
      alert('League settings updated successfully. All league participants will be notified of these changes.');
    } catch (err: any) {
      alert('Failed to update settings: ' + (err.message || 'Unknown error'));
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-primary mb-4">League Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Name:</span>
              <span className="font-medium">{league.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Type:</span>
              <span className="font-medium capitalize">{league.league_type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                league.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {league.status}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Visibility:</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                league.is_public ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'
              }`}>
                {league.is_public ? 'Public' : 'Private'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Participants:</span>
              <span className="font-medium">{league.current_participants}/{league.max_participants}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Created:</span>
              <span className="font-medium">{new Date(league.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <h3 className="text-lg font-semibold text-primary mb-4">Tournament Information</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Tournament:</span>
              <span className="font-medium">{league.tournament_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Format:</span>
              <span className="font-medium">{league.format}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Year:</span>
              <span className="font-medium">{league.year}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
        <h3 className="text-lg font-semibold text-primary mb-4">Manager Information</h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Manager:</span>
            <span className="font-medium">{league.manager_name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Email:</span>
            <span className="font-medium">{league.manager_email}</span>
          </div>
        </div>
      </div>
    </div>
  );

  const renderParticipants = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-primary">League Participants</h3>
        <span className="text-sm text-gray-500">{participants.length} participants</span>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 text-primary font-bold">User</th>
                <th className="text-left py-4 px-6 text-primary font-bold">Role</th>
                <th className="text-left py-4 px-6 text-primary font-bold">Joined</th>
                <th className="text-center py-4 px-6 text-primary font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {participants.map((participant) => (
                <tr key={participant.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200">
                  <td className="py-4 px-6">
                    <div>
                      <p className="font-medium text-primary">{participant.display_name}</p>
                      <p className="text-sm text-gray-500">{participant.email}</p>
                    </div>
                  </td>
                  <td className="py-4 px-6">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      participant.role === 'manager' 
                        ? 'bg-accent text-primary' 
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {participant.role}
                    </span>
                  </td>
                  <td className="py-4 px-6 text-gray-600">
                    {new Date(participant.joined_at).toLocaleDateString()}
                  </td>
                  <td className="text-center py-4 px-6">
                    <div className="flex items-center justify-center space-x-2">
                      {participant.role === 'manager' ? (
                        <>
                          <button
                            onClick={() => handleRemoveAdmin(participant.id)}
                            disabled={updatingParticipant === participant.id}
                            className="text-orange-600 hover:text-orange-800 transition-colors duration-200"
                            title="Remove Admin Role"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleRemoveParticipant(participant.id)}
                            disabled={updatingParticipant === participant.id}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title="Remove Participant"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handlePromoteToAdmin(participant.id)}
                            disabled={updatingParticipant === participant.id}
                            className="text-green-600 hover:text-green-800 transition-colors duration-200"
                            title="Promote to Admin"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleRemoveParticipant(participant.id)}
                            disabled={updatingParticipant === participant.id}
                            className="text-red-600 hover:text-red-800 transition-colors duration-200"
                            title="Remove Participant"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderSettings = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-primary">League Settings</h3>
        <span className="text-sm text-gray-500">Admin Override</span>
      </div>
      {!loading && settings ? (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="space-y-8">
            {/* Core Points System - Always Enabled */}
            <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
              <h3 className="text-lg font-semibold text-blue-800 border-b border-blue-300 pb-2 mb-4">
                🎯 Core Points System (Always Active)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-blue-700 mb-1">
                    Correct Pick Points
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-blue-600 bg-blue-100 px-2 py-1 rounded">Default: 100</span>
                    <input
                      type="number"
                      value={settings.correct_pick_points}
                      onChange={(e) => setSettings({ ...settings, correct_pick_points: e.target.value })}
                      className="flex-1 px-3 py-2 border border-blue-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Your Setting"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Wrong Pick Penalty */}
            <div className="bg-red-50 p-6 rounded-lg border border-red-200">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-red-800">❌ Wrong Pick Penalty</h3>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.wrong_pick_enabled === 'true'}
                    onChange={(e) => setSettings({ ...settings, wrong_pick_enabled: e.target.checked ? 'true' : 'false' })}
                    className="mr-2 w-4 h-4 text-red-600 bg-white/10 border-red-300 rounded focus:ring-red-500 focus:ring-2"
                  />
                  <span className="text-sm font-medium text-red-700">Enable</span>
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-red-700 mb-1">
                    Wrong Pick Points
                  </label>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-red-600 bg-red-100 px-2 py-1 rounded">Default: -10</span>
                    <input
                      type="number"
                      value={settings.wrong_pick_points}
                      onChange={(e) => setSettings({ ...settings, wrong_pick_points: e.target.value })}
                      className="flex-1 px-3 py-2 border border-red-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                      placeholder="Your Setting"
                      disabled={settings.wrong_pick_enabled !== 'true'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Streak Penalties */}
            <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
              <h3 className="text-lg font-semibold text-orange-800 mb-4">�� Streak Penalties</h3>
              <div className="space-y-4">
                {/* Wrong 5 Picks in a Row */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-orange-700">Wrong 5 Picks in a Row</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.wrong_5_picks_streak_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, wrong_5_picks_streak_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-orange-600 bg-white/10 border-orange-300 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-orange-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">Default: -10</span>
                    <input
                      type="number"
                      value={settings.wrong_5_picks_streak_penalty}
                      onChange={(e) => setSettings({ ...settings, wrong_5_picks_streak_penalty: e.target.value })}
                      className="flex-1 px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Your Setting"
                      disabled={settings.wrong_5_picks_streak_enabled !== 'true'}
                    />
                  </div>
                </div>
                {/* Wrong 10 Picks in a Row */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-orange-700">Wrong 10 Picks in a Row</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.wrong_10_picks_streak_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, wrong_10_picks_streak_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-orange-600 bg-white/10 border-orange-300 rounded focus:ring-orange-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-orange-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-orange-600 bg-orange-100 px-2 py-1 rounded">Default: -25</span>
                    <input
                      type="number"
                      value={settings.wrong_10_picks_streak_penalty}
                      onChange={(e) => setSettings({ ...settings, wrong_10_picks_streak_penalty: e.target.value })}
                      className="flex-1 px-3 py-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Your Setting"
                      disabled={settings.wrong_10_picks_streak_enabled !== 'true'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Streak Bonuses */}
            <div className="bg-green-50 p-6 rounded-lg border border-green-200">
              <h3 className="text-lg font-semibold text-green-800 mb-4">📈 Streak Bonuses</h3>
              <div className="space-y-4">
                {/* Correct 5 Picks in a Row */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-green-700">Correct 5 Picks in a Row</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.correct_5_picks_streak_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, correct_5_picks_streak_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-green-600 bg-white/10 border-green-300 rounded focus:ring-green-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-green-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">Default: 10</span>
                    <input
                      type="number"
                      value={settings.correct_5_picks_streak_bonus}
                      onChange={(e) => setSettings({ ...settings, correct_5_picks_streak_bonus: e.target.value })}
                      className="flex-1 px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your Setting"
                      disabled={settings.correct_5_picks_streak_enabled !== 'true'}
                    />
                  </div>
                </div>
                {/* Correct 10 Picks in a Row */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-green-700">Correct 10 Picks in a Row</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.correct_10_picks_streak_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, correct_10_picks_streak_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-green-600 bg-white/10 border-green-300 rounded focus:ring-green-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-green-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded">Default: 25</span>
                    <input
                      type="number"
                      value={settings.correct_10_picks_streak_bonus}
                      onChange={(e) => setSettings({ ...settings, correct_10_picks_streak_bonus: e.target.value })}
                      className="flex-1 px-3 py-2 border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="Your Setting"
                      disabled={settings.correct_10_picks_streak_enabled !== 'true'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Total Runs Prediction */}
            <div className="bg-purple-50 p-6 rounded-lg border border-purple-200">
              <h3 className="text-lg font-semibold text-purple-800 mb-4">🔢 Total Runs Prediction</h3>
              <div className="space-y-4">
                {/* Exact Prediction */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-purple-700">Exact Prediction</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.total_runs_exact_prediction_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, total_runs_exact_prediction_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-purple-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 50</span>
                    <input
                      type="number"
                      value={settings.total_runs_exact_prediction_points}
                      onChange={(e) => setSettings({ ...settings, total_runs_exact_prediction_points: e.target.value })}
                      className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Your Setting"
                      disabled={settings.total_runs_exact_prediction_enabled !== 'true'}
                    />
                  </div>
                </div>
                {/* Close Prediction */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-purple-700">Close Prediction (±5)</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.total_runs_close_prediction_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, total_runs_close_prediction_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-purple-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 20</span>
                    <input
                      type="number"
                      value={settings.total_runs_close_prediction_points}
                      onChange={(e) => setSettings({ ...settings, total_runs_close_prediction_points: e.target.value })}
                      className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Your Setting"
                      disabled={settings.total_runs_close_prediction_enabled !== 'true'}
                    />
                  </div>
                </div>
                {/* Medium Prediction */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-purple-700">Medium Prediction (±10)</label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings.total_runs_medium_prediction_enabled === 'true'}
                        onChange={(e) => setSettings({ ...settings, total_runs_medium_prediction_enabled: e.target.checked ? 'true' : 'false' })}
                        className="mr-2 w-4 h-4 text-purple-600 bg-white/10 border-purple-300 rounded focus:ring-purple-500 focus:ring-2"
                      />
                      <span className="text-sm font-medium text-purple-700">Enable</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-purple-600 bg-purple-100 px-2 py-1 rounded">Default: 10</span>
                    <input
                      type="number"
                      value={settings.total_runs_medium_prediction_points}
                      onChange={(e) => setSettings({ ...settings, total_runs_medium_prediction_points: e.target.value })}
                      className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      placeholder="Your Setting"
                      disabled={settings.total_runs_medium_prediction_enabled !== 'true'}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Man of the Match Prediction */}
            <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
              <h3 className="text-lg font-semibold text-yellow-800 mb-4">🏅 Man of the Match Prediction</h3>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-yellow-700">Enable Man of the Match Prediction</label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={settings.man_of_match_prediction_enabled === 'true'}
                    onChange={(e) => setSettings({ ...settings, man_of_match_prediction_enabled: e.target.checked ? 'true' : 'false' })}
                    className="mr-2 w-4 h-4 text-yellow-600 bg-white/10 border-yellow-300 rounded focus:ring-yellow-500 focus:ring-2"
                  />
                  <span className="text-sm font-medium text-yellow-700">Enable</span>
                </label>
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-yellow-600 bg-yellow-100 px-2 py-1 rounded">Default: 50</span>
                <input
                  type="number"
                  value={settings.man_of_match_prediction_points}
                  onChange={(e) => setSettings({ ...settings, man_of_match_prediction_points: e.target.value })}
                  className="flex-1 px-3 py-2 border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  placeholder="Your Setting"
                  disabled={settings.man_of_match_prediction_enabled !== 'true'}
                />
              </div>
            </div>

            {/* Admin Override Notice */}
            <div className="pt-6 border-t border-gray-200">
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Admin Override Notice</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Any changes made here will override the league manager's settings and notify all participants.</p>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => handleUpdateSettings(settings)}
                className="bg-accent text-primary px-6 py-3 rounded-lg font-medium hover:bg-accent/90 transition-colors duration-200"
              >
                Update Settings & Notify Participants
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
            <p className="text-gray-600">Loading league settings...</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-secondary p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-white">{league.name}</h2>
              <p className="text-blue-100">{league.tournament_name} • {league.format} • {league.year}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-100 transition-colors duration-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex space-x-1 p-4">
            {[
              { key: 'overview', label: 'Overview', icon: '📊' },
              { key: 'participants', label: 'Participants', icon: '👥' },
              { key: 'settings', label: 'Settings', icon: '⚙️' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-accent text-primary'
                    : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading league data...</p>
            </div>
          ) : (
            <>
                          {activeTab === 'overview' && renderOverview()}
            {activeTab === 'participants' && renderParticipants()}
            {activeTab === 'settings' && renderSettings()}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// TournamentsPage component (original tournament management functionality)
const TournamentsPage = () => {
  const [activeTab, setActiveTab] = useState<'visible' | 'all' | 'cricapi'>('visible');
  const [visibleTournaments, setVisibleTournaments] = useState<any[]>([]);
  const [allTournaments, setAllTournaments] = useState<any[]>([]);
  const [cricapiTournaments, setCricapiTournaments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedCricAPITournaments, setSelectedCricAPITournaments] = useState<string[]>([]);
  const [importing, setImporting] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Track which tabs have been loaded to avoid redundant API calls
  const [loadedTabs, setLoadedTabs] = useState<Set<string>>(new Set());

  const fetchVisibleTournaments = async () => {
    try {
      const data = await apiService.getVisibleTournamentsAdmin();
      setVisibleTournaments(data.tournaments || []);
    } catch (err: any) {
      console.error('Failed to load visible tournaments:', err);
    }
  };

  const fetchAllTournaments = async () => {
    try {
      const data = await apiService.getImportedTournamentsAdmin();
      setAllTournaments(data.tournaments || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load imported tournaments');
    }
  };

  const fetchCricAPITournaments = async () => {
    try {
      console.log('🔍 Fetching CricAPI tournaments...');
      const data = await apiService.getCricAPITournaments();
      setCricapiTournaments(data.tournaments || []);
      console.log('✅ CricAPI tournaments loaded:', data.tournaments?.length || 0);
    } catch (err: any) {
      console.error('Failed to load CricAPI tournaments:', err);
      setError(err.message || 'Failed to load CricAPI tournaments');
    }
  };

  // Load data for a specific tab only when needed
  const loadTabData = async (tab: string) => {
    if (loadedTabs.has(tab)) {
      console.log(`🔍 Tab ${tab} already loaded, skipping API call`);
      return;
    }

    console.log(`🔍 Loading data for tab: ${tab}`);
    setLoading(true);
    
    try {
      switch (tab) {
        case 'visible':
          await fetchVisibleTournaments();
          break;
        case 'all':
          await fetchAllTournaments();
          break;
        case 'cricapi':
          await fetchCricAPITournaments();
          break;
      }
      
      setLoadedTabs(prev => new Set([...Array.from(prev), tab]));
    } catch (err: any) {
      console.error(`Failed to load ${tab} tab:`, err);
    } finally {
      setLoading(false);
    }
  };

  // Load initial tab data
  useEffect(() => {
    loadTabData('visible');
  }, []);

  // Load data when tab changes
  useEffect(() => {
    loadTabData(activeTab);
  }, [activeTab]);

  const handleToggleVisibility = async (tournament: any) => {
    setUpdatingId(tournament.id);
    try {
      await apiService.updateTournamentAdmin(tournament.id, { is_visible: !tournament.is_visible });
      // Refresh only the relevant tabs
      if (activeTab === 'visible') {
        await fetchVisibleTournaments();
      }
      if (activeTab === 'all') {
        await fetchAllTournaments();
      }
    } catch (err: any) {
      alert('Failed to update visibility: ' + (err.message || 'Unknown error'));
    }
    setUpdatingId(null);
  };

  const handleCricAPISelection = (tournamentId: string) => {
    setSelectedCricAPITournaments(prev => 
      prev.includes(tournamentId) 
        ? prev.filter(id => id !== tournamentId)
        : [...prev, tournamentId]
    );
  };

  const handleImportCricAPITournaments = async () => {
    if (selectedCricAPITournaments.length === 0) {
      alert('Please select at least one tournament to import.');
      return;
    }

    setImporting(true);
    try {
      const result = await apiService.importCricAPITournaments(selectedCricAPITournaments);
      alert(`Import completed!\nImported: ${result.imported}\nFailed: ${result.failed}`);
      setSelectedCricAPITournaments([]);
      
      // Refresh only the relevant tabs after import
      await fetchVisibleTournaments();
      await fetchAllTournaments();
      
      // Clear the CricAPI tab cache to force a fresh load next time
      setLoadedTabs(prev => {
        const newSet = new Set(prev);
        newSet.delete('cricapi');
        return newSet;
      });
    } catch (err: any) {
      alert('Failed to import tournaments: ' + (err.message || 'Unknown error'));
    }
    setImporting(false);
  };

  const handleRefreshCricAPI = async () => {
    console.log('🔄 Manually refreshing CricAPI data...');
    setLoading(true);
    try {
      // Clear the cache to force a fresh fetch
      setLoadedTabs(prev => {
        const newSet = new Set(prev);
        newSet.delete('cricapi');
        return newSet;
      });
      
      // Fetch fresh data
      await fetchCricAPITournaments();
      
      // Mark as loaded
      setLoadedTabs(prev => new Set([...Array.from(prev), 'cricapi']));
    } catch (err: any) {
      console.error('Failed to refresh CricAPI data:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderTournamentTable = (tournaments: any[], showVisibilityToggle: boolean = false, showSource: boolean = false) => (
    <table className="min-w-full bg-white/10 rounded-lg overflow-hidden">
      <thead>
        <tr>
          {activeTab === 'cricapi' && (
            <th className="px-4 py-2 text-left text-white">Select</th>
          )}
          <th className="px-4 py-2 text-left text-white">Name</th>
          <th className="px-4 py-2 text-left text-white">Format</th>
          <th className="px-4 py-2 text-left text-white">Status</th>
          {showSource && (
            <th className="px-4 py-2 text-left text-white">Source</th>
          )}
          {showVisibilityToggle && (
            <th className="px-4 py-2 text-left text-white">Visible</th>
          )}
          {showVisibilityToggle && (
            <th className="px-4 py-2 text-left text-white">Actions</th>
          )}
        </tr>
      </thead>
      <tbody>
        {tournaments.map(t => (
          <tr key={t.id} className="border-b border-white/10">
            {activeTab === 'cricapi' && (
              <td className="px-4 py-2">
                <input
                  type="checkbox"
                  checked={selectedCricAPITournaments.includes(t.id)}
                  onChange={() => handleCricAPISelection(t.id)}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
              </td>
            )}
            <td className="px-4 py-2 text-white">{t.name}</td>
            <td className="px-4 py-2 text-white">{t.format}</td>
            <td className="px-4 py-2 text-white">{t.status}</td>
            {showSource && (
              <td className="px-4 py-2 text-white">{t.source || 'database'}</td>
            )}
            {showVisibilityToggle && (
              <td className="px-4 py-2 text-white">{t.is_visible ? 'Yes' : 'No'}</td>
            )}
            {showVisibilityToggle && (
              <td className="px-4 py-2">
                <button
                  className={`px-3 py-1 rounded ${t.is_visible ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'} text-white font-semibold transition-colors text-sm`}
                  onClick={() => handleToggleVisibility(t)}
                  disabled={updatingId === t.id}
                >
                  {updatingId === t.id ? 'Updating...' : t.is_visible ? 'Hide' : 'Show'}
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-6">Tournament Management</h2>
      <p className="text-white/80 mb-6">Manage tournament visibility for regular users.</p>
      
      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-white/10 rounded-lg p-1">
        <button
          onClick={() => setActiveTab('visible')}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
            activeTab === 'visible'
              ? 'bg-accent text-primary'
              : 'text-white/70 hover:text-white'
          }`}
        >
          User Visible ({visibleTournaments.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
            activeTab === 'all'
              ? 'bg-accent text-primary'
              : 'text-white/70 hover:text-white'
          }`}
        >
          Imported Tournaments ({allTournaments.length})
        </button>
        <button
          onClick={() => setActiveTab('cricapi')}
          className={`flex-1 py-2 px-4 rounded-md font-semibold transition-colors ${
            activeTab === 'cricapi'
              ? 'bg-accent text-primary'
              : 'text-white/70 hover:text-white'
          }`}
        >
          From CricAPI ({cricapiTournaments.length})
        </button>
      </div>
      
      {/* Content */}
      {loading ? (
        <div className="text-white text-center py-8">Loading tournaments...</div>
      ) : error ? (
        <div className="text-red-400 text-center py-8">{error}</div>
      ) : (
        <div>
          {activeTab === 'visible' && (
            <div>
              <h3 className="text-xl font-semibold text-white mb-4">
                Currently Visible to Users
              </h3>
              <p className="text-white/60 mb-4">
                These imported tournaments are visible to regular users and guests.
              </p>
              {visibleTournaments.length === 0 ? (
                <div className="text-white/60 text-center py-8">No visible tournaments found.</div>
              ) : (
                renderTournamentTable(visibleTournaments, false, true)
              )}
            </div>
          )}

          {activeTab === 'cricapi' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Available from CricAPI
                  </h3>
                  <p className="text-white/60">
                    Select tournaments to import into your system. Imported tournaments will be hidden by default.
                    <br />
                    <span className="text-yellow-400 text-sm">💡 Tip: Use "Refresh CricAPI" only when you need fresh data to avoid hitting API limits.</span>
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <span className="text-white/60 text-sm">
                    Selected: {selectedCricAPITournaments.length}
                  </span>
                  <button
                    onClick={async () => {
                      try {
                        // Clear the cache first
                        await apiService.clearCricAPICache();
                        console.log('🗑️ CricAPI cache cleared');
                        
                        // Clear the frontend cache
                        setLoadedTabs(prev => {
                          const newSet = new Set(Array.from(prev));
                          newSet.delete('cricapi');
                          return newSet;
                        });
                        
                        // Reload the data
                        await loadTabData('cricapi');
                      } catch (err) {
                        console.error('Failed to refresh CricAPI data:', err);
                        alert('Failed to refresh CricAPI data');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors"
                  >
                    🔄 Refresh CricAPI
                  </button>
                  <button
                    onClick={handleImportCricAPITournaments}
                    disabled={selectedCricAPITournaments.length === 0 || importing}
                    className={`px-4 py-2 rounded-lg font-semibold transition-colors ${
                      selectedCricAPITournaments.length === 0 || importing
                        ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 text-white'
                    }`}
                  >
                    {importing ? 'Importing...' : 'Import Selected'}
                  </button>
                </div>
              </div>
              {cricapiTournaments.length === 0 ? (
                <div className="text-white/60 text-center py-8">No tournaments found from CricAPI.</div>
              ) : (
                renderTournamentTable(cricapiTournaments, false, true)
              )}
            </div>
          )}

          {activeTab === 'all' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-white mb-0">
                  Imported Tournaments (Manage Visibility)
                </h3>
                <button
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
                  onClick={() => setShowCreateModal(true)}
                >
                  + Add Tournament
                </button>
              </div>
              <CreateTournamentModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onCreated={fetchAllTournaments}
              />
              {allTournaments.length === 0 ? (
                <div className="text-white/60 text-center py-8">No imported tournaments found. Import tournaments from the "From CricAPI" tab.</div>
              ) : (
                renderTournamentTable(allTournaments, true, true)
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// --- API Fetch Response History Utility ---
const API_FETCH_HISTORY_KEY = 'apiFetchHistory';

function getApiFetchHistory(): Record<string, any[]> {
  try {
    const raw = localStorage.getItem(API_FETCH_HISTORY_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveApiFetchHistory(history: Record<string, any[]>) {
  localStorage.setItem(API_FETCH_HISTORY_KEY, JSON.stringify(history));
}

function addApiFetchResponse(endpoint: string, response: any) {
  const history = getApiFetchHistory();
  if (!history[endpoint]) history[endpoint] = [];
  history[endpoint].unshift({
    timestamp: new Date().toISOString(),
    response
  });
  if (history[endpoint].length > 10) history[endpoint] = history[endpoint].slice(0, 10);
  saveApiFetchHistory(history);
}

// --- ApiFetchHistoryPage ---
const ApiFetchHistoryPage = ({ onBack }: { onBack: () => void }) => {
  const [activeTab, setActiveTab] = useState<string>('');
  const [expanded, setExpanded] = useState<{[key: string]: boolean}>({});
  const history = getApiFetchHistory();
  const endpoints = Object.keys(history);

  // Default to first endpoint
  React.useEffect(() => {
    if (endpoints.length && !activeTab) setActiveTab(endpoints[0]);
  }, [endpoints, activeTab]);

  return (
    <div className="p-6">
      <button
        onClick={onBack}
        className="mb-4 px-4 py-2 bg-gray-700 hover:bg-gray-800 text-white rounded-lg font-semibold"
      >
        ← Back to Fetch API
      </button>
      <h2 className="text-2xl font-bold text-white mb-6">API Fetch Response History</h2>
      {endpoints.length === 0 ? (
        <div className="text-white/70">No fetch history yet.</div>
      ) : (
        <>
          {/* Tabs for endpoints */}
          <div className="flex gap-2 mb-6">
            {endpoints.map((ep) => (
              <button
                key={ep}
                onClick={() => setActiveTab(ep)}
                className={`px-4 py-2 rounded-lg font-semibold transition-colors ${activeTab === ep ? 'bg-accent text-primary' : 'bg-white/10 text-white hover:bg-white/20'}`}
              >
                {ep}
              </button>
            ))}
          </div>
          {/* Response list for active endpoint */}
          <div>
            {(history[activeTab] || []).map((entry, idx) => (
              <div key={idx} className="mb-4 bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white/70 text-xs">{new Date(entry.timestamp).toLocaleString()}</span>
                  <button
                    onClick={() => setExpanded(e => ({...e, [idx]: !e[idx]}))}
                    className="text-accent text-sm font-semibold"
                  >
                    {expanded[idx] ? 'Minimize' : 'Maximize'}
                  </button>
                </div>
                {expanded[idx] ? (
                  <pre className="text-green-400 text-xs whitespace-pre-wrap bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
                    {JSON.stringify(entry.response, null, 2)}
                  </pre>
                ) : (
                  <pre className="text-green-400 text-xs whitespace-pre overflow-hidden max-h-16 bg-gray-900 rounded-lg p-2">
                    {JSON.stringify(entry.response, null, 2).slice(0, 200)}{JSON.stringify(entry.response, null, 2).length > 200 ? '... (maximized to view more)' : ''}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// --- FetchAPIPage (update) ---
const FetchAPIPage = () => {
  const [customEndpoint, setCustomEndpoint] = useState('');
  const [apiResponse, setApiResponse] = useState<any>(null);
  const [loading, setLoading] = useState<{[endpoint: string]: boolean}>({});
  const [error, setError] = useState<string | null>(null);
  const [endpointParams, setEndpointParams] = useState<{[endpoint: string]: {[paramName: string]: string}}>({});
  const [showHistory, setShowHistory] = useState(false);

  // Pre-configured API endpoints
  const predefinedEndpoints = [
    {
      name: 'Series List',
      endpoint: '/series',
      description: 'Fetch all available series/tournaments from CricAPI',
      requiresParams: false
    },
    {
      name: 'Series Info',
      endpoint: '/series_info',
      description: 'Get detailed information about a specific series',
      requiresParams: true,
      paramName: 'id',
      paramLabel: 'Series ID',
      paramPlaceholder: 'Enter series ID (e.g., 1234)',
      paramExample: '1234'
    },
    {
      name: 'Series Squad',
      endpoint: '/series_squad',
      description: 'Get squad/player information for a specific series',
      requiresParams: true,
      paramName: 'id',
      paramLabel: 'Series ID',
      paramPlaceholder: 'Enter series ID (e.g., 1234)',
      paramExample: '1234'
    },
    {
      name: 'Series Matches',
      endpoint: '/series_matches',
      description: 'Get all matches in a series',
      requiresParams: true,
      paramName: 'id',
      paramLabel: 'Series ID',
      paramPlaceholder: 'Enter Series ID',
      paramExample: '1234',
      comingSoon: true, // Mark as coming soon
    },
    {
      name: 'All Matches',
      endpoint: '/matches',
      description: 'Get all matches',
      requiresParams: false,
      comingSoon: false,
    },
    {
      name: 'Match Info',
      endpoint: '/match_info',
      description: 'Get detailed match information',
      requiresParams: true,
      paramName: 'id',
      paramLabel: 'Match ID',
      paramPlaceholder: 'Enter match ID (e.g., 5678)',
      paramExample: '5678'
    }
  ];

  const handleFetchAPI = async (endpoint: string, params?: string) => {
    setLoading(l => ({ ...l, [endpoint]: true }));
    setError(null);
    setApiResponse(null);
    try {
      const fullEndpoint = params ? `${endpoint}${params}` : endpoint;
      console.log(`🔍 Fetching API: ${fullEndpoint}`);
      
      let response;
      if (endpoint === '/series_squad') {
        // Special handling for series squad endpoint (POST request)
        const seriesId = params?.replace('?id=', '') || '';
        response = await apiService.fetchSeriesSquad(seriesId);
      } else {
        response = await apiService.fetchCricAPIEndpoint(fullEndpoint);
      }
      
      setApiResponse(response);
      addApiFetchResponse(endpoint, response); // Save to history
      console.log('✅ API Response:', response);
    } catch (err: any) {
      console.error('❌ API Error:', err);
      setError(err.message || 'Failed to fetch API data');
    } finally {
      setLoading(l => ({ ...l, [endpoint]: false }));
    }
  };

  const handlePredefinedFetch = async (endpoint: string, requiresParams: boolean, paramName?: string) => {
    if (requiresParams && paramName) {
      const paramValue = endpointParams[endpoint]?.[paramName];
      if (!paramValue || !paramValue.trim()) {
        setError(`Please enter a ${paramName} value`);
        return;
      }
      const params = `?${paramName}=${encodeURIComponent(paramValue.trim())}`;
      await handleFetchAPI(endpoint, params);
    } else {
      await handleFetchAPI(endpoint);
    }
  };

  const handleCustomFetch = async () => {
    if (!customEndpoint.trim()) {
      setError('Please enter an endpoint');
      return;
    }
    await handleFetchAPI(customEndpoint);
  };

  const handleParamChange = (endpoint: string, paramName: string, value: string) => {
    setEndpointParams(prev => ({
      ...prev,
      [endpoint]: {
        ...(prev[endpoint] || {}),
        [paramName]: value
      }
    }));
  };

  if (showHistory) {
    return <ApiFetchHistoryPage onBack={() => setShowHistory(false)} />;
  }

  return (
    <div>
      <h2 className="text-3xl font-bold text-white mb-6">Fetch API</h2>
      <p className="text-white/80 mb-6">Manually fetch data from CricAPI endpoints. Use this to control API usage and fetch specific data.</p>
      
      {/* Pre-configured Endpoints */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-white mb-4">Pre-configured Endpoints</h3>
        <div className="grid gap-4">
          {predefinedEndpoints.map((ep, index) => (
            <div key={index} className="bg-white/10 rounded-lg p-4">
              <div className="flex justify-between items-start mb-2">
                <div className="flex-1">
                  <h4 className="text-white font-semibold">{ep.name}</h4>
                  <p className="text-white/60 text-sm">{ep.description}</p>
                  <code className="text-accent text-sm">{ep.endpoint}</code>
                  {ep.requiresParams && ep.paramExample && (
                    <p className="text-yellow-400 text-xs mt-1">
                      Example: {ep.paramExample}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handlePredefinedFetch(ep.endpoint, ep.requiresParams, ep.paramName)}
                  disabled={ep.comingSoon || loading[ep.endpoint]}
                  className={`px-4 py-2 ${ep.comingSoon ? 'bg-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'} disabled:bg-gray-600 text-white rounded-lg font-semibold transition-colors ml-4`}
                >
                  {ep.comingSoon ? 'Coming Soon' : (loading[ep.endpoint] ? 'Fetching...' : 'Fetch')}
                </button>
                <button
                  onClick={() => setShowHistory(true)}
                  disabled={ep.comingSoon}
                  className={`ml-2 px-4 py-2 ${ep.comingSoon ? 'bg-gray-500 cursor-not-allowed' : 'bg-accent'} text-primary rounded-lg font-semibold hover:bg-accent/80 transition-colors`}
                >
                  View Response
                </button>
              </div>
              
              {/* Parameter input field */}
              {ep.requiresParams && ep.paramName && (
                <div className="mt-3">
                  <label className="block text-white text-sm font-medium mb-1">
                    {ep.paramLabel}
                  </label>
                  <input
                    type="text"
                    value={endpointParams[ep.endpoint]?.[ep.paramName] || ''}
                    onChange={(e) => handleParamChange(ep.endpoint, ep.paramName!, e.target.value)}
                    placeholder={ep.paramPlaceholder}
                    className="w-full px-3 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-accent"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Custom Endpoint */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-white mb-4">Custom Endpoint</h3>
        <div className="bg-white/10 rounded-lg p-4">
          <div className="flex gap-4 mb-4">
            <input
              type="text"
              value={customEndpoint}
              onChange={(e) => setCustomEndpoint(e.target.value)}
              placeholder="Enter CricAPI endpoint (e.g., /series_info?id=1234)"
              className="flex-1 px-4 py-2 bg-white/20 border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-accent"
            />
            <button
              onClick={handleCustomFetch}
              disabled={loading[customEndpoint] || !customEndpoint.trim()}
              className="px-6 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded-lg font-semibold transition-colors"
            >
              {loading[customEndpoint] ? 'Fetching...' : 'Fetch'}
            </button>
          </div>
          <p className="text-white/60 text-sm">
            💡 Enter any CricAPI endpoint. Include parameters if needed (e.g., ?id=1234&apikey=your_key)
          </p>
        </div>
      </div>

      {/* Response Display */}
      {loading[customEndpoint] && (
        <div className="text-white text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent mx-auto mb-2"></div>
          Fetching API data...
        </div>
      )}

      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-4">
          <h4 className="text-red-400 font-semibold mb-2">Error</h4>
          <p className="text-red-300">{error}</p>
        </div>
      )}

      {apiResponse && (
        <div className="bg-white/10 rounded-lg p-4">
          <h3 className="text-xl font-semibold text-white mb-4">API Response</h3>
          <div className="bg-gray-900 rounded-lg p-4 overflow-auto max-h-96">
            <pre className="text-green-400 text-sm whitespace-pre-wrap">
              {JSON.stringify(apiResponse, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

function AppContent(props: any) {
  const { isAuthenticated, user } = useAuth();
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A1833] via-[#13294B] to-[#4FC3F7]">
      {/* Header */}
      <Header onLogin={props.openLoginModal} onRegister={props.openRegisterModal} />
      {/* Main Content */}
      <Routes>
        <Route path="/" element={
          <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="text-center">
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
                Fantasy Cricket Reimagined
              </h2>
              <p className="text-xl text-white/80 mb-8 max-w-3xl mx-auto">
                Pick&apos;em is live — predict match winners and climb the leaderboard. Explore Budget and H2H leagues, with more modes on the way.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/tournaments" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg text-lg font-semibold transition-all transform hover:scale-105">
                  Start Playing
                </Link>
              </div>
            </div>
            {/* Tournament Types */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <Link to="/pickem" className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 cursor-pointer">
                <div className="text-3xl mb-4">🎯</div>
                <h3 className="text-xl font-semibold text-white mb-2">Pick&apos;em</h3>
                <p className="text-white/70">Predict match outcomes and earn points based on accuracy.</p>
              </Link>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 relative cursor-not-allowed opacity-80">
                <div className="absolute top-2 right-2 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                  Coming Soon
                </div>
                <div className="text-3xl mb-4">💀</div>
                <h3 className="text-xl font-semibold text-white mb-2">Eliminator</h3>
                <p className="text-white/70">Survival tournament — get eliminated after wrong picks.</p>
              </div>
              <Link to="/h2h" className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 cursor-pointer">
                <div className="text-3xl mb-4">⚔️</div>
                <h3 className="text-xl font-semibold text-white mb-2">H2H Matches</h3>
                <p className="text-white/70">Head-to-head battles in single match competitions.</p>
              </Link>
              <Link to="/budget-league" className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 hover:bg-white/20 transition-all transform hover:scale-105 cursor-pointer">
                <div className="text-3xl mb-4">💰</div>
                <h3 className="text-xl font-semibold text-white mb-2">Budget League</h3>
                <p className="text-white/70">Build teams within budget constraints and compete for prizes.</p>
              </Link>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20 relative cursor-not-allowed opacity-80">
                <div className="absolute top-2 right-2 bg-amber-500 text-black text-xs font-bold px-2 py-1 rounded-full">
                  Coming Soon
                </div>
                <div className="text-3xl mb-4">📋</div>
                <h3 className="text-xl font-semibold text-white mb-2">Draft League</h3>
                <p className="text-white/70">Draft players in a snake draft format with friends.</p>
              </div>
            </div>
            {/* Features */}
            <div className="mt-16">
              <h3 className="text-3xl font-bold text-white text-center mb-8">Platform Features</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-blue-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🌍</span>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">3 Cricket Formats</h4>
                  <p className="text-white/70">ODI, T20, and T10 cricket formats supported.</p>
                </div>
                <div className="text-center">
                  <div className="bg-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">🏆</span>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">Real-time Scoring</h4>
                  <p className="text-white/70">Live updates and automatic score calculations.</p>
                </div>
                <div className="text-center">
                  <div className="bg-purple-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl">👥</span>
                  </div>
                  <h4 className="text-xl font-semibold text-white mb-2">Community</h4>
                  <p className="text-white/70">Connect with cricket fans worldwide.</p>
                </div>
              </div>
            </div>
          </main>
        } />
        <Route path="/tournaments" element={<Tournaments />} />
        <Route path="/tournament/:id" element={<TournamentDetails />} />
        <Route path="/live-matches" element={<LiveMatches />} />
        <Route path="/pickem" element={<PickemPage />} />
        <Route path="/budget-league" element={<BudgetLeagueOverview />} />
        <Route path="/budget-league/:id" element={<BudgetLeaguePage />} />
        <Route path="/draft-league" element={<DraftLeaguePage />} />
        <Route path="/h2h" element={<H2HPage />} />
        <Route path="/h2h-league/:id" element={<H2HLeaguePage />} />
        <Route path="/eliminator" element={<EliminatorPage />} />
        <Route path="/create-league" element={<CreateLeaguePage />} />
                        <Route path="/pickem-league/:id" element={<PickemLeaguePage />} />
                <Route path="/eliminator-league/:id" element={<EliminatorLeaguePage />} />
        <Route path="/join-league" element={<JoinLeaguePage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/my-leagues" element={<MyLeagues />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/admin" element={
          isAuthenticated && user?.is_admin ? (
            <AdminPanel />
          ) : (
            <main className="max-w-2xl mx-auto px-4 py-12 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">Access Denied</h2>
              <p className="text-white/80">You must be an admin to view this page.</p>
            </main>
          )
        } />
        <Route path="/admin/tournament/:id" element={<AdminTournamentDetails />} />
      </Routes>
      {/* Footer */}
      <footer className="bg-black/20 border-t border-white/20 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-white/60">
            <p>&copy; 2024 CricFantasyPros. All rights reserved.</p>
          </div>
        </div>
      </footer>
      {/* Auth Modals */}
      <AuthModals
        isLoginModalOpen={props.isLoginModalOpen}
        isRegisterModalOpen={props.isRegisterModalOpen}
        isForgotPasswordModalOpen={props.isForgotPasswordModalOpen}
        closeModals={props.closeModals}
        openRegisterModal={props.openRegisterModal}
        openLoginModal={props.openLoginModal}
        openForgotPasswordModal={props.openForgotPasswordModal}
      />
    </div>
  );
}

function App() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);

  const openLoginModal = () => {
    setIsLoginModalOpen(true);
    setIsRegisterModalOpen(false);
  };

  const openRegisterModal = () => {
    setIsRegisterModalOpen(true);
    setIsLoginModalOpen(false);
  };

  const openForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(true);
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
  };

  const closeModals = () => {
    setIsLoginModalOpen(false);
    setIsRegisterModalOpen(false);
    setIsForgotPasswordModalOpen(false);
  };

  // Proper forgot password handler
  const handleForgotPassword = async (email: string) => {
    await apiService.forgotPassword(email);
  };

  return (
    <AuthProvider>
      <Router>
        <AppContent
          openLoginModal={openLoginModal}
          openRegisterModal={openRegisterModal}
          openForgotPasswordModal={openForgotPasswordModal}
          closeModals={closeModals}
          isLoginModalOpen={isLoginModalOpen}
          isRegisterModalOpen={isRegisterModalOpen}
          isForgotPasswordModalOpen={isForgotPasswordModalOpen}
        />
      </Router>
    </AuthProvider>
  );
}

export default App;
